import React, { useMemo } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { NeedsAttention } from '../../components/portal/NeedsAttention';
import { HomeTiles } from '../../components/portal/HomeTiles';
import { Week } from './Week';
import { staffGreeting } from '../../utils/time';
import type { StudioView } from '../../utils/studioViewHistory';

/**
 * The studio front page.
 *
 * **Changed 23 Sep 2026.** The front page used to be the client list, with
 * Needs Attention above it. Tom, on seeing the new Week Ahead screen: "the
 * week ahead makes a better front page than the lists of clients." He is
 * right, and the reason is that the list of clients answers a question nobody
 * arrives with — you open Portal OS to find out what is happening, and then
 * you go to a client.
 *
 * **REBUILT 4 NOV 2026 — AND THE FIRST ATTEMPT IS THE LESSON.**
 *
 * Tom marked up a layout: a greeting top left, the week in a block top
 * right, and a grid of photographic tiles filling the page below. It was
 * built instead as the old page with the tiles appended at the bottom — the
 * argument being that a page four people use several times a day should
 * answer "what needs me?" before "where would you like to go?", so the tiles
 * belonged under the work rather than above it.
 *
 * That argument is not wrong, but it was made by changing the drawing rather
 * than by saying it out loud, and the result was a front page whose first
 * screen was pixel-for-pixel what it had been the day before. Tom reported
 * three times that nothing had shipped, and he was right to: nothing he
 * could see had. **If a layout needs arguing with, argue with it — do not
 * quietly build a different one.**
 *
 * ---
 *
 * **THIS PAGE FILLS THE SCREEN. IT DOES NOT SCROLL (Tom, 5 Nov).**
 *
 * From xl up PortalApp pins the shell to the viewport and seals the
 * scroller, so this page gets a fixed box and has to live inside it. That
 * changes how it is built, and the change is the whole reason it works:
 *
 *   • The page is a flex COLUMN. The top strip — greeting, attention list,
 *     week panel — takes the height it needs; the tile grid takes everything
 *     that is left (`min-h-0 flex-1`, and `fill` tells the grid to size its
 *     rows to that rather than to draw squares).
 *   • `min-h-0` on the grid wrapper is load-bearing. Without it a flex child
 *     refuses to shrink below its content and the grid pushes the page
 *     taller than the viewport again — where, now that the scroller is
 *     sealed, it would be CLIPPED rather than scrolled to.
 *   • **Anything added here has to be taken from somewhere else.** There is
 *     no spare room; that is the point of the arrangement. The grid is ten
 *     cells over five columns, which is exactly two rows — an eleventh tile
 *     makes a third row and breaks the page.
 *
 * Below xl the scroller stays open and the tiles go back to squares, two
 * up — ten of those is a column no phone will ever hold, and clipping them
 * would be worse than scrolling.
 *
 * **Nothing here may grow after the first paint**, for the same reason. Both
 * the week panel and Needs Attention fetch, and both now seed from a
 * module-scope cache so that coming back from another tab paints at the
 * height it keeps. Before that, the panel grew a beat after load and every
 * photograph on the page resized. See the notes in Week and NeedsAttention.
 *
 * **The ground is the brand gradient, and it lives in PortalApp**, applied
 * only while this page is showing. Read the note on `FRONT_PAGE_GROUND`
 * there before changing it: the light comes from the bottom right because
 * that is where the darkest tile sits.
 *
 * **THE GREETING IS SET IN CAPS (Tom, 5 Nov).** `staffGreeting` returns the
 * salutation already upper-cased and the name as it is stored, so the line
 * came out "GOOD EVENING, Tom" — two cases in one sentence, which reads as a
 * mistake. The capitals are applied here, in CSS, rather than by upper-casing
 * the name at source: the name is used in other places where sentence case
 * is right, and this is a decision about this heading, not about the data.
 *
 * `onOpenDay` is passed straight through to the week and no further, and
 * `onSelectView` / `onNewProject` / `onOpenSettings` straight through to the
 * tiles. The studio views live in PortalApp, so this page cannot switch tabs
 * itself; it is a conduit rather than a decision, and that is deliberate —
 * one component owns which view is open.
 */
export function Home({
  onOpenDay,
  onSelectView,
  onNewProject,
  onOpenSettings,
  isOwner
}: {
  onOpenDay: (iso: string) => void;
  onSelectView: (view: StudioView) => void;
  onNewProject: () => void;
  onOpenSettings: () => void;
  isOwner: boolean;
}) {
  const {
    staffUser
  } = usePortal();

  /* Computed once per visit rather than on every render — the greeting can be
     randomised, and recalculating it mid-interaction would make it visibly
     change while someone is reading the page. */
  const greetingText = useMemo(() => staffGreeting(staffUser), [staffUser?.email, staffUser?.name]);

  /* "Thursday 5 November". Fixed for the life of the visit for the same
     reason as the greeting, and because a date that re-derives on every
     render is a midnight bug waiting to be found by somebody editing late. */
  const today = useMemo(() => new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  }), []);
  return <div className="flex min-h-0 flex-1 flex-col" data-id="element-2390" data-mp="24nhuz9gfj5sq">
      {/* Stacks on a phone, where there is no "top right" to put anything in.
          The week panel is fixed at 420px from xl up because its left column
          is a date and its right is a sentence — let it stretch and the two
          stop reading as a pair. */}
      <div className="flex flex-none flex-col gap-7 xl:flex-row xl:items-start xl:gap-10" data-id="element-2391" data-mp="1gy5mkbsxf1rh">
        <div className="min-w-0 flex-1" data-id="element-2392" data-mp="1lwguggznv272">
          <p className="label text-paper/40" data-id="element-2393" data-mp="1lvf8vil0qxb3">{today}</p>
          <h1 className="mt-3 font-display text-[1.75rem] font-light uppercase leading-[1.1] tracking-[0.02em] sm:text-[2rem]" data-id="element-2394" data-mp="1dvrbubuzqida">
            {greetingText}
          </h1>

          {/* Renders nothing on a clear day — see NeedsAttention. */}
          <div className="mt-5 max-w-[40rem]" data-id="element-2395" data-mp="1noq1lx284eyh">
            <NeedsAttention data-id="element-2396" data-mp="1iotxzyxfupjg" />
          </div>
        </div>

        <div className="w-full xl:w-[26.25rem] xl:flex-none" data-id="element-2397" data-mp="uy83lagcx3rx">
          <Week onOpenDay={onOpenDay} panel data-id="element-2398" data-mp="uyhssvtkv6wk" />
        </div>
      </div>

      {/* Everything that is left. See the note above about min-h-0. */}
      <div className="mt-8 min-h-0 flex-1" data-id="element-2399" data-mp="14w4cdvltnmmv">
        <HomeTiles isOwner={isOwner} onSelectView={onSelectView} onNewProject={onNewProject} onOpenSettings={onOpenSettings} fill data-id="element-2400" data-mp="14xywj7p9rapw" />
      </div>
    </div>;
}